import { placeClickToCall } from '../../lib/clickToCall';
import { PatientLinkControl } from '../../components/PatientContactLink';
import { stageOptionsFrom, labelForStage, LEGACY_STAGE_LABELS } from '../../lib/contactStageOptions';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  ArrowLeft, Phone, Mail, MessageSquare, Tag, UserCheck, DollarSign, Calendar,
  CheckSquare, FileText, Activity, Zap, CreditCard, Plus, Send, Edit2, X,
  Clock, AlertTriangle, Award, Target, ChevronDown, ChevronUp, RefreshCw,
  User, Users, MapPin, Globe, Briefcase, Heart, Pill, Trash2, AtSign,

  Stethoscope,

} from 'lucide-react';
import {
  Badge, neuShadow, neuShadowSm, neuShadowInset,
  inp, btnPrimary, btnSm, btnDanger, cardWrap, emptyMsg,
  overlayStyle, modalStyle, modalHeader, modalTitle,
  CONTACT_TYPES, TASK_PRIORITY,
} from './crmShared';
import TreatmentPlanModal from './TreatmentPlanModal';
import ClinicalAITab from './ClinicalAITab';
import ContactFieldsPanel, { NextActionPill } from './ContactFieldsPanel';
import AccountabilityPanel from '../../components/AccountabilityPanel';
import ContactTagPicker from './ContactTagPicker';
import AddTaskModal from './AddTaskModal';
import ContactPEMTab from './ContactPEMTab';
import ContactPhysicianTab from './ContactPhysicianTab';
import CallSurfaceTab from './CallSurfaceTab';
import LeadIntakePanel from '../../components/LeadIntakePanel';
import PatientIntakeSection from '../../components/PatientIntakeSection';
import DiscoveryCallPanel from '../../components/DiscoveryCallPanel';
import TreatmentAdminPanel from '../../components/TreatmentAdminPanel';
import GroupedTabNav from './GroupedTabNav';
import { useNavGuard } from '../../lib/NavHistory';
import useUnsavedWarning from '../../lib/useUnsavedWarning';
import { readParam, writeParams } from '../../lib/urlState';

// ─── Constants ───────────────────────────────────────────────────────────────

const TABS = [
  { id: 'profile', label: 'Profile', icon: UserCheck },
  // PEM workflow — Task #51/#52: journey lifecycle + concierge messaging +
  // follow-up cadence + review requests, all inline on the contact view
  // per Carlos's 2026-06-08 role definition. Positioned second so it sits
  // right after Profile, where the PEM lives day-to-day.
  { id: 'patient_journey', label: 'Patient Journey', icon: Activity },
  // Guided discovery call — Phase 5g-a / Luisa R5 (Job Aide 9-step flow).
  { id: 'discovery', label: 'Discovery Call', icon: Activity },
  // Physician workflow — Task #62: candidacy + safety screen + treatment
  // recommendation. Adjacent to Patient Journey so the two halves of
  // Carlos's spec ("The Physician Diagnoses. The PEM Coordinates.") sit
  // side by side. Visibility gated on physician:read at render time.
  { id: 'physician', label: 'Physician', icon: Stethoscope, requiresPermission: 'physician:read' },
  { id: 'conversations', label: 'Conversations', icon: MessageSquare },
  // Patient Call Surface — Phase 5g-b.4: per-contact call history, recording
  // playback, transcripts, and click-to-call.
  { id: 'calls', label: 'Calls', icon: Phone },
  { id: 'appointments', label: 'Appointments', icon: Calendar },
  { id: 'opportunities', label: 'Opportunities', icon: DollarSign },
  { id: 'treatments', label: 'Treatments', icon: Heart },
  { id: 'clinical_ai', label: 'Clinical AI', icon: Stethoscope },
  { id: 'team', label: 'Team', icon: Users },
  { id: 'activities', label: 'Activities', icon: Activity },
  { id: 'tasks', label: 'Tasks', icon: CheckSquare },
  { id: 'notes', label: 'Notes', icon: FileText },
  { id: 'workflows', label: 'Workflows', icon: Zap },
  { id: 'payments', label: 'Payments', icon: CreditCard },
];

/*
 * ⛔ CONSUMED ONCE PER PAGE LOAD — Card PARTNER-LIST-REFRESH.
 *
 * `?ctab=` answers "which tab was open when the browser reloaded", and that
 * question has exactly one answer per page load.
 *
 * ⚠ THIS COMMENT USED TO SAY ContactDetailView IS KEYED BY contactId AND IT IS
 * NOT — corrected in PR #618 review round 4. Neither CRMPage nor ContactDrawer
 * passes a `key`, deliberately: keying it would reset the open tab on every
 * contact switch. (The `key={selectedContactId}` near CRMPage.js:1360 belongs
 * to the component ABOVE this one, which is what made the claim look true.)
 *
 * The latch does not depend on a remount — it is MODULE-SCOPED and resets only
 * on a real page load, which is exactly when `?ctab=` has a fresh answer. So
 * the mechanism is unchanged; only its stated reason was wrong. Without the
 * latch, switching from a contact open on Care to a different contact would
 * silently open the new one on Care too, because the URL still said so. Opening someone else's record on the tab
 * you happened to be reading is exactly the kind of quiet wrongness that
 * erodes trust in a clinical surface.
 *
 * Module scope, not a ref: the latch must outlive the component, because the
 * component is what remounts. It resets naturally on a real page load, which
 * is the only time the URL's answer is fresh.
 */
let _urlTabConsumed = false;

/** @returns {string} the tab id from `?ctab=`, once, or '' thereafter. */
function consumeUrlTab(validIds) {
  if (_urlTabConsumed) return '';
  _urlTabConsumed = true;
  const want = readParam('ctab');
  return want && validIds.has(want) ? want : '';
}

/** Test seam: let a test exercise the first-load path more than once. */
export function __resetUrlTabConsumedForTests() { _urlTabConsumed = false; }

const PLAN_STATUS_COLORS = {
  draft: { color: '#94A3B8', bg: '#F1F5F9' },
  active: { color: '#15803D', bg: '#DCFCE7' },
  paused: { color: '#B45309', bg: '#FEF3C7' },
  completed: { color: '#2E8B98', bg: '#DBEAFE' },
  cancelled: { color: '#DC3545', bg: '#FEE2E2' },
};

const OPP_STATUSES = {
  open: { label: 'Open', color: '#2E8B98', bg: '#E0F2FE' },
  won: { label: 'Won', color: '#4FA038', bg: '#DCFCE7' },
  lost: { label: 'Lost', color: '#DC3545', bg: '#FEE2E2' },
  abandoned: { label: 'Abandoned', color: '#6C757D', bg: '#F3F4F6' },
};

/*
 * ⛔ Card P116b — THIS LIST IS NO LONGER THE ANSWER, ONLY THE FALLBACK.
 *
 * Maria reported "Disqualified - Not a Fit" missing from ZGX while it sits in
 * GHL. It was never missing from the database: Daniel's pipeline-coverage
 * census (26 Oct 02:11 UTC, exit 0 PASS) matched all 16 GHL pipelines and
 * every stage count, so the stage is in crm_pipeline_stages. It was missing
 * from HERE — eleven hard-coded keys that cannot name Carlos's canonical
 * stages, while routes/gohighlevel.js:1197 writes exactly those canonical
 * names into crm_contacts.pipeline_stage.
 *
 * ⚠ The eleven keys are STILL NEEDED and are kept in ../../lib/
 * contactStageOptions.js: a contact with no opportunity has no pipeline (see
 * that file's header for why), and its stage really is one of these codes.
 * The real stages come from the server; these catch everything else.
 */

const STAGE_COLORS = {
  lead: '#6C757D', contacted: '#2E8B98', qualified: '#256F7A',
  proposal: '#F26522', negotiation: '#D97706', closed_won: '#4FA038', closed_lost: '#DC3545',
};

const APPT_STATUS_COLORS = { scheduled: '#2E8B98', confirmed: '#62BB46', completed: '#4FA038', cancelled: '#6C757D', no_show: '#DC3545', rescheduled: '#F26522' };

const INTERACTION_ICONS = {
  phone_call: Phone, email: Mail, in_person: User, text_message: MessageSquare,
  video_call: Globe, fax: FileText, letter: FileText, portal_message: Globe, other: Activity,
};

// ─── Main Component ──────────────────────────────────────────────────────────

export default function ContactDetailView({
  contactId, onBack, allUsers, canUpdate, onOpenPatient,
  // Card P100b — the patient-side permissions, passed from CRMPage.
  canCreatePatient = false, canLinkPatient = false, canUnlinkPatient = false,
  // Optional — passed from CRMPage so the SMS / Email buttons up top can
  // open the global SendMessageModal directly (with AI Suggest). When not
  // provided, the buttons fall back to navigating to the Conversations tab.
  setShowSendModal, setSendChannel, setSendPrefill, setSendResult,
  // Optional — fired whenever the contact is patched via the Profile tab
  // or Next Action pill. CRMPage uses this to refresh its contacts list so
  // the smart-filter chips reflect the latest field values without a hard
  // refresh.
  onContactUpdated,
  // Task #62/#63 (Jun 8 2026): user is needed to derive physician:read,
  // physician:write, physician:sign for the new Physician tab. Optional
  // for backwards compatibility — if omitted, the Physician tab hides.
  user,
  // Optional deep-link sub-tab — when the Physician Dashboard opens a
  // contact, it passes 'physician' so we land directly on the workflow
  // tab. Consumed once via the callback then cleared by the parent.
  initialTab,
  onInitialTabConsumed,
}) {
  const userPermissions = user?.permissions || [];
  const canSeePhysician = userPermissions.includes('physician:read');
  const canWritePhysician = userPermissions.includes('physician:write');
  const canSignPhysician = userPermissions.includes('physician:sign');
  /**
   * ⛔⛔ physician:sign IS NOT treatment_proposal:sign — Card P77, 10 Oct 2026.
   *
   * MEASURED off database/rbac.js:
   *   physician:sign          admin, physician, treating_physician,
   *                           chief_medical_director, sysadmin
   *   treatment_proposal:sign admin, physician, sysadmin
   *
   * The proposal card was gating its buttons on canSignPhysician while the
   * handlers behind them call rbac.secureHandler("treatment_proposal:sign").
   * So a treating_physician or a chief_medical_director saw an ENABLED
   * "Generate Treatment Plan", clicked it, and the server refused — the
   * #615 "buttons that agreed with you and did nothing" shape, one layer up.
   *
   * ⚠ THE SUPERSET IS WHY NOBODY NOTICED. Every role that holds
   * treatment_proposal:sign also holds physician:sign, so the wrong gate is
   * correct for admin, physician and sysadmin — which is everyone who tests
   * it. It is wrong only for the two roles nobody logs in as.
   *
   * Passed as separate props rather than widening the physician ones: these
   * are different permissions with different holders, and collapsing them is
   * how they drifted apart in the first place.
   */
  const canReadProposal = userPermissions.includes('treatment_proposal:read');
  const canWriteProposal = userPermissions.includes('treatment_proposal:write');
  const canSignProposal = userPermissions.includes('treatment_proposal:sign');
  const canWriteProposalDoc = userPermissions.includes('treatment_proposal_document:write');
  // #136: owner reassignment control (gated by the crm:reassign permission).
  const canReassign = userPermissions.includes('crm:reassign');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  /*
   * ⚠ Card P100 — null means NOT YET ASKED. That is a third state and it is
   * shown as "Checking…": offering "Link existing patient" while the lookup
   * is in flight invites a doctor to link a contact that is already linked.
   */
  const [patientLink, setPatientLink] = useState(null);

  const [tab, setTab] = useState(
    () => consumeUrlTab(new Set(TABS.map((t) => t.id))) || 'conversations'
  );
  const [showTagPanel, setShowTagPanel] = useState(true);

  // Task #63: consume the initialTab handoff exactly once on mount so deep
  // links from the Physician Dashboard land on the Physician sub-tab. We
  // use a ref-guarded effect so re-renders (e.g. data reloads) don't keep
  // resetting the tab if the user later clicks a different one.
  const initialTabConsumedRef = useRef(false);
  useEffect(() => {
    if (initialTabConsumedRef.current) return;
    if (initialTab) {
      initialTabConsumedRef.current = true;
      setTab(initialTab);
      if (typeof onInitialTabConsumed === 'function') onInitialTabConsumed();
    }
  }, [initialTab, onInitialTabConsumed]);

  // Inline edit mode for the Contact Info card.
  // Basic patient fields are edited in the Patient intake section on the
  // Profile tab, which saves per-field through crm:contacts:patch-intake.
  // This view no longer calls crm:contacts:update at all.
  // which stamps last_modified_source='zignagenix' and (per PR #21) eventually
  // pushes back to GHL.

  // ⚠ DIRTY-FORM GUARD (#65 C3) — the other half of the Back step.
  // C2b-2 (#292) deferred this page rather than half-wire it, for exactly this
  // reason: making the contact a Back step without a guard would let Back,
  // Alt+←, the history dropdown or the Android hardware button silently discard
  // a rep's in-progress edit. The step and this guard ship together or not at
  // all — do not remove one and keep the other.
  //
  // Called UNCONDITIONALLY here, above the loading / no-data early returns
  // below, per the repo-wide react-hooks/rules-of-hooks gate. The message is
  // GENERIC: the guard registry holds only a boolean and a static string, never
  // a patient name or a record id.
  /*
   * ⛔⛔ A HALF-TYPED NOTE IS PHI, AND IT WAS BEING LOST SILENTLY.
   *
   * `isEditing` covered the contact-info form. The NOTE textarea was never
   * covered by anything: navigating away in-app, or reloading, discarded it
   * with no prompt. Card PARTNER-LIST-REFRESH makes reloading a thing staff
   * will now do on purpose (the record comes back), so the silent loss gets
   * MORE likely, not less — which is why both halves ship together.
   *
   * ⛔ The draft is NOT saved anywhere. See src/lib/useUnsavedWarning.js: a
   * clinical note in localStorage or sessionStorage is plaintext PHI on a
   * possibly-shared workstation, and CrmFilterBar already refuses to persist
   * far less than that. We warn; if the human says go ahead, it is gone.
   *
   * ⚠ Only a BOOLEAN leaves this component. Neither the guard registry nor the
   * beforeunload handler ever sees the note text, the contact name or the id.
   */
  /*
    ⛔⛔ THE CONTACT RECORD COULD NOT ADD A TASK — Card CONTACT-PROFILE-INTAKE
    item 12. The header's "+ Task" button called setTab('tasks') and stopped
    there; TasksPane had no create control, so the rep arrived at a list and no
    way to add to it. Opening the real modal, prefilled and locked to THIS
    contact, is the whole fix — the channel was always registered.
  */
  const [showAddTask, setShowAddTask] = useState(false);
  /*
   * ⛔ Card P91 item 5: the Call button is DISABLED while a call is in flight.
   * Twilio rings the rep's own phone, so a second click while the first is
   * ringing places a SECOND call to the same rep — two legs, one human.
   * `callStatus` carries the honest line shown underneath.
   */
  const [calling, setCalling] = useState(false);
  const [callStatus, setCallStatus] = useState(null);
  const [noteDirty, setNoteDirty] = useState(false);
  /*
   * ⚠ `isEditing` DROPPED FROM THIS EXPRESSION — Card CONTACT-PROFILE-INTAKE.
   * It tracked the side-panel contact-info form, which no longer exists: those
   * fields moved to the Patient intake section, which AUTOSAVES and flushes any
   * pending debounce on unmount, so there is no window in which leaving the
   * page discards a typed value. The note textarea still has one, and still
   * warns.
   */
  const hasUnsaved = noteDirty;

  // In-app navigation (Back, keyboard, sidebar) — can be CANCELLED.
  useNavGuard({
    when: hasUnsaved,
    message: 'You have unsaved changes on this contact. Leave without saving?',
  });
  // The browser (reload, tab close, address bar) — cannot be cancelled by us,
  // only prompted. Nothing in the React tree can see these events, which is
  // why a second mechanism is needed rather than a wider useNavGuard.
  useUnsavedWarning(hasUnsaved, 'You have an unsaved note on this contact.');

  // Mirror the open tab so a reload comes back to it. Written on every change
  // (not only on unmount) because a reload is not a graceful teardown — an
  // unmount-time write would never run for the one event it exists to survive.
  useEffect(() => { writeParams({ ctab: tab }); }, [tab]);

  /*
    ⛔⛔ A LOAD THAT RESOLVES AFTER THE REP HAS MOVED ON MUST NOT LAND —
    PR #618 review round 4, blocking item (b).

    `setData(result)` ran unconditionally. A `loadDetail(A)` started by A's
    own onPatched just before a switch could resolve AFTER `loadDetail(B)`,
    putting A's record into `data` while `contactId` is already B. The intake
    panel is keyed by contactId, so it stayed mounted as B's instance holding
    A's row — and B's next edit went to A.

    ⚠ THE GUARD IS ON THE REQUEST, NOT ON THE PAYLOAD'S id — and that is a
    deliberate limit, not an omission.

    My first cut ALSO dropped any payload whose own `id` differed from the one
    asked for. That broke tests/react/ContactDetailIdHeader.test.js, and
    rightly: issue #373 exists because a note meant for contact 6107 was added
    to 8391 twice, and the fix was to render the LOADED payload's id so a
    server answering with the wrong record is VISIBLE on screen. Dropping such
    a payload would leave the previous contact displayed with no indication at
    all — hiding exactly the failure the id was put there to surface.

    ⇒ A mismatched payload is still shown, still labelled with its real id, and
    the Patient intake section refuses to render for it (see the gate at its
    mount). Visible, and not editable.
  */
  const loadSeqRef = useRef(0);
  const liveContactIdRef = useRef(contactId);
  liveContactIdRef.current = contactId;

  const loadDetail = useCallback(async () => {
    const forId = contactId;

    /*
      ⛔⛔ RETURN BEFORE THE INCREMENT — PR #618 review round 5, blocking.

      The counter used to be bumped first, which let a STALE caller strand the
      view. `onPatched` is captured at render, so ContactFieldsPanel.commit,
      NextActionPill and ContactTagPicker all hold a loadDetail bound to the
      contact that was open when they rendered. Sequence:

        1. the rep edits a field on A, then clicks B
        2. loadDetail(B) goes out as seq n
        3. A's blur commit resolves; its captured onPatched calls loadDetail(A)
        4. that bumped the counter to n+1 — INVALIDATING B's in-flight response
        5. …and then A's own response was dropped by the live-id check below

      Both were discarded, so `data` stayed on A while contactId was B: the
      header showed A, the intake said "Loading…" forever, and Notes listed A's
      notes while Add note posted to B. A guard that strands the screen on the
      wrong patient is worse than the race it was added to stop.

      ⇒ A caller that is no longer current does not get to touch the counter at
      all. It is not an error — it is a reply to a question nobody is asking
      any more.
    */
    if (String(forId) !== String(liveContactIdRef.current)) return;

    const seq = ++loadSeqRef.current;
    /** Still the request the screen is waiting for? */
    const current = () => seq === loadSeqRef.current
      && String(forId) === String(liveContactIdRef.current);

    setLoading(true);
    setLoadError(null);
    try {
      const result = await window.zignagenixAPI.getCRMContactFullDetail(forId);
      // ⛔ Superseded, or the view has moved on: drop it entirely.
      if (!current()) return;
      if (result && !result.error) setData(result);
      else {
        setLoadError(result?.error || 'Contact data could not be retrieved.');
        console.error('[ContactDetail] error:', result);
      }
    } catch (e) {
      /*
        ⛔ The SAME check on the failure path: a stale connection error must not
        blank the record the rep is actually looking at.

        ⚠⚠ THIS GUARD HAS NO INDEPENDENT RED ARM, AND THAT IS STATED RATHER
        THAN GLOSSED. Measured: planting it away leaves every test green,
        because `loadError` is rendered ONLY inside the `if (!data)` branch
        (line ~413) and the `finally` guard below keeps `loading` true for a
        superseded request — so a stale rejection cannot reach that render path
        today. It is kept as defence in depth: the day someone surfaces
        `loadError` as a toast beside live data, this line is what stops a
        failure from a request the rep abandoned appearing over the record they
        are reading. The behaviour that IS tested is "a stale rejection does not
        blank the live record", whose red arm is the `finally` guard.
      */
      if (!current()) return;
      setLoadError('A connection error occurred. Please check your network and try again.');
      console.error('[ContactDetail] load error:', e);
    }
    finally {
      // ⚠ Only the CURRENT request owns the spinner. A superseded one clearing
      // it would report "loaded" while the live request is still outstanding.
      if (seq === loadSeqRef.current) setLoading(false);
    }
  }, [contactId]);

  useEffect(() => { loadDetail(); }, [loadDetail]);

  /*
   * ── Card P116b: the REAL stages this contact's pipeline offers ───────────
   *
   * ⛔⛔ A CONTACT HAS NO pipeline_id. The server derives the pipeline from the
   * contact's opportunities and returns that pipeline's stages; see
   * ../../lib/contactStageOptions.js for the full derivation and why a
   * contact with no deal legitimately has none.
   *
   * ⛔ A FAILURE HERE IS NOT AN EMPTY STAGE LIST. stageOptionsFrom(null, …)
   * returns the legacy codes, so a dropped call degrades to exactly the
   * behaviour that shipped before this card rather than removing the control.
   */
  const [stageMeta, setStageMeta] = useState(() => stageOptionsFrom(null, null));
  const loadStageOptions = useCallback(async (forId, currentStage) => {
    if (!forId) { setStageMeta(stageOptionsFrom(null, null)); return; }
    let payload = null;
    try {
      const api = window.zignagenixAPI;
      if (api && api.getContactStageOptions) {
        payload = await api.getContactStageOptions({ contactId: forId });
      }
    } catch { payload = null; }
    // ⚠ Guard against a superseded record the same way loadDetail does: the
    // options belong to a contact, and the wrong contact's stages are worse
    // than none.
    if (String(forId) !== String(liveContactIdRef.current)) return;
    setStageMeta(stageOptionsFrom(payload, currentStage));
  }, []);

  useEffect(() => {
    if (!data || !data.id) return;
    loadStageOptions(data.id, data.pipeline_stage);
  }, [data, loadStageOptions]);

  /*
   * Write the stage. Goes through the EXISTING crm:contacts:move-stage
   * channel, which already wraps the update in the transaction that sets
   * zg.user_id + zg.source='ui' so migration 154's recorder trigger attributes
   * the move. Adding a second write path would have left the stage history
   * with un-attributed rows from this screen only.
   *
   * ⛔ The optimistic update is NOT the record. loadDetail() re-reads, so if
   * the write was refused the select snaps back to what the database holds
   * rather than showing a stage nobody stored.
   */
  const [savingStage, setSavingStage] = useState(false);
  const [stageError, setStageError] = useState(null);
  const changeStage = useCallback(async (next) => {
    if (!next || !data || next === data.pipeline_stage) return;
    setSavingStage(true); setStageError(null);
    try {
      const r = await window.zignagenixAPI.moveContactStage(data.id, next);
      if (!r || r.success === false) { setStageError(r?.error || 'stage change refused'); return; }
      loadDetail();
      if (onContactUpdated) onContactUpdated();
    } catch (e) {
      setStageError(e.message);
    } finally {
      setSavingStage(false);
    }
  }, [data, loadDetail, onContactUpdated]);

  /*
   * ⛔ Card P100 — is this contact already a patient? Asked once per record,
   * alongside the detail load. The answer decides which button the header
   * shows, and "not yet asked" shows neither.
   */
  useEffect(() => {
    let cancelled = false;
    const id = data && data.id;
    if (!id) { setPatientLink(null); return undefined; }
    (async () => {
      try {
        const r = await window.zignagenixAPI.invoke('crm:contact-patient-link', id);
        if (!cancelled) setPatientLink(r || { linked: false });
      } catch { if (!cancelled) setPatientLink({ linked: false }); }
    })();
    return () => { cancelled = true; };
  }, [data]);



  /**
   * ⛔⛔ THE FULL-PAGE SPINNER IS FOR THE FIRST LOAD ONLY — Card CRM-SCROLL-JUMP,
   * 8 Oct 2026.
   *
   * `if (loading)` replaced the ENTIRE detail view — header, tab strip, every
   * field — on every REFRESH, not just the first load. And a refresh happens on
   * every field save: ContactFieldsPanel autosaves, calls onPatched, and the
   * four call sites below (:311, :315, :545, :672) call loadDetail(), which
   * sets loading=true. React unmounted the whole tree and rebuilt it.
   *
   * ⚠⚠ THAT IS WHAT DANIEL DEMONSTRATED TO CARLOS AND RYLAND: "the screen goes
   * back to the top and you must scroll down to start filling out the second
   * part of the form". Staff do this while on the phone with a consultation, so
   * every click cost a scroll.
   *
   * ⚠ NOTHING EVER SCROLLED. There is no window.scrollTo in this path — the
   * page scrolls in an inner overflowY:'auto' container (CRMPage.js:729-736),
   * and when its content is swapped for a short spinner the browser CLAMPS
   * scrollTop to 0. The scroll position was not moved; the scrollable content
   * stopped existing.
   *
   * ⇒ `loading && !data` keeps the spinner for the genuine first load and lets
   * a refresh happen UNDERNEATH the rendered form. Per-field "saving…" from
   * ContactFieldsPanel's FieldStatus is the in-place indicator; a full-page
   * loader was never the right shape for a single-field autosave.
   */
  if (loading && !data) return (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300 }}>
      <div style={{
        background: '#e0e5ec', borderRadius: 16, padding: '40px 48px',
        boxShadow: neuShadow, textAlign: 'center', maxWidth: 380,
      }}>
        <RefreshCw size={36} color="#2E8B98" style={{ marginBottom: 16, animation: 'spin 1.2s linear infinite' }} />
        <div style={{ fontSize: 16, fontWeight: 700, color: '#212529', marginBottom: 6 }}>Loading Contact</div>
        <div style={{ fontSize: 13, color: '#6C757D' }}>Retrieving contact details, please wait...</div>
      </div>
    </div>
  );

  if (!data) return (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300 }}>
      <div style={{
        background: '#e0e5ec', borderRadius: 16, padding: '40px 48px',
        boxShadow: neuShadow, textAlign: 'center', maxWidth: 420,
      }}>
        <AlertTriangle size={40} color="#F26522" style={{ marginBottom: 16 }} />
        <div style={{ fontSize: 18, fontWeight: 700, color: '#212529', marginBottom: 8 }}>Data Not Available</div>
        <div style={{ fontSize: 13, color: '#6C757D', marginBottom: 20, lineHeight: 1.5 }}>
          {loadError || 'The contact information could not be loaded. This may be because the record no longer exists or there was a connection issue.'}
        </div>
        <button
          onClick={onBack}
          style={{
            ...btnPrimary, background: '#2E8B98', display: 'inline-flex',
            alignItems: 'center', gap: 6, padding: '10px 24px', fontSize: 14,
          }}
        >
          <ArrowLeft size={16} /> Go Back
        </button>
      </div>
    </div>
  );

  /*
    ⛔⛔ ONE PREDICATE FOR "THE ROW ON SCREEN IS THE ROW YOU OPENED" —
    PR #618 review round 5, item 2.

    Round 4 gated only the Patient intake section on this. That was too narrow:
    while `data` and `contactId` disagree, EVERY write surface on this screen is
    aimed at the wrong patient, and they are aimed in TWO different directions,
    which is what makes it hard to see.

      - NotesPane, TeamPane, TreatmentsPane, DiscoveryCallPanel,
        LeadIntakePanel, ContactPEMTab and the Tasks pane take `contactId` and
        write to the REQUESTED contact — while the header shows the payload's.
      - ContactFieldsPanel, NextActionPill and ContactTagPicker take `contact`
        and write to the PAYLOAD'S id — which is not the record the rep opened.

    Either way the rep is looking at one patient and editing another. Call, SMS
    and Email are in the same class: they send to `data.phone` / `data.email`,
    so a mismatch means messaging the wrong person.

    ⇒ Identity stays VISIBLE (issue #373 — the header still shows the loaded
    id, so a wrong-record answer is seen, not hidden). Nothing that WRITES
    renders until the two agree.
  */
  const idsMatch = String(data?.id ?? '') === String(contactId);
  /** The single gate every write control on this screen is derived from. */
  const canWrite = canUpdate && idsMatch;

  const fullName = `${data.first_name || ''} ${data.last_name || ''}`.trim();
  const contactType = CONTACT_TYPES[data.contact_type] || { label: data.contact_type, color: '#6C757D' };
  const totalOppValue = (data.opportunities || []).reduce((s, o) => s + Number(o.value || 0), 0);

  return (
    <div>
      {/* ═══ BACK BUTTON ═══ */}
      <button onClick={onBack} style={{ ...btnSm, marginBottom: 12, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
        <ArrowLeft size={14} /> Back to List
      </button>

      {/* ⇣ REP ACCOUNTABILITY MOVED BELOW — Card CONTACT-PROFILE-INTAKE item 7.
          Carlos: "Lead score, at-risk flags and Rep Accountability move below
          it." The rep opens a record to fill the intake form while the patient
          is on the phone; the scoring chrome is for reading the record later,
          and it was occupying the first screenful. It now renders under the tab
          content — see LEAD HEALTH + REP ACCOUNTABILITY at the foot of this
          component. Nothing about it changed except where it sits. */}

      {!idsMatch && (
        <div
          role="status"
          data-testid="contact-identity-banner"
          style={{
            ...cardWrap, padding: '10px 14px', marginBottom: 12,
            background: '#FEF3C7', border: '1px solid #FBBF24',
            color: '#78350F', fontSize: 12, fontWeight: 600,
            display: 'flex', alignItems: 'center', gap: 8,
          }}
        >
          <AlertTriangle size={14} color="#B45309" />
          {/* ⚠ THE BANNER DOES NOT REPEAT THE RECORD ID. The header already
              shows it (issue #373), and printing it twice made
              ContactDetailIdHeader's `/ID[^0-9]*9999/` matcher find two
              elements — a test that exists to prove the id is visible, broken
              by saying it again. One place states the identity. */}
          {data?.id == null
            ? 'Loading the selected contact — editing is paused until it arrives.'
            : "This record doesn't match the one you opened — editing is disabled. Refresh to try again."}
        </div>
      )}

      {/* ═══ 1. HEADER — QUICK ACTION ZONE ═══ */}
      <div style={{ ...cardWrap, padding: '20px 24px', marginBottom: 16 }}>
        <div style={{ display: 'flex', gap: 20, alignItems: 'flex-start', flexWrap: 'wrap' }}>
          {/* Avatar + Name */}
          <div style={{ display: 'flex', gap: 14, alignItems: 'center', flex: '1 1 300px' }}>
            <div style={{ width: 56, height: 56, borderRadius: 14, background: `linear-gradient(135deg, ${contactType.color}, ${contactType.color}88)`,
              display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <span style={{ color: '#fff', fontSize: 20, fontWeight: 700 }}>{(data.first_name?.[0] || '?').toUpperCase()}{(data.last_name?.[0] || '').toUpperCase()}</span>
            </div>
            <div>
              <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: '#212529' }}>{fullName || 'Unknown'}</h2>
              {/*
                ⛔⛔ CARD P100 — THE PHYSICIAN WORKFLOW HANGS OFF THIS CONTACT.
                Candidacy, the safety screen and treatment proposals are all
                written here, while the doctor's chart is a PATIENT record.
                Unlinked, the work has nowhere to land and
                lead-intake:convert-to-emr refuses outright.
                ⚠ These buttons are a courtesy; the server refuses regardless.
              */}
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 4, flexWrap: 'wrap' }}>
                <PatientLinkControl
                  link={patientLink}
                  contact={data}
                  canCreatePatient={canCreatePatient}
                  canLink={canLinkPatient}
                  canUnlink={canUnlinkPatient}
                  onOpenPatient={onOpenPatient}
                  onChanged={setPatientLink}
                />
              </div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 4, flexWrap: 'wrap' }}>
                {/* ⚠ RECORD ID (#373). There is no router and no URL, so this is
                    the ONLY way a user can confirm from the screen which record
                    is open. On 2026-08-21 a note intended for one contact was
                    added to another TWICE, discoverable only afterwards from
                    audit_log. This screen also edits pipeline stage, owner
                    assignment and soft-delete.
                    ⚠ Reads data.id — the LOADED payload — never the contactId
                    prop. Echoing the prop would show the id we asked for even
                    when the backend returned a different record, which is the
                    exact failure this is here to make visible. */}
                <span
                  style={{
                    fontSize: 12, color: '#6C757D', fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                    background: '#f1f3f5', borderRadius: 4, padding: '1px 6px', whiteSpace: 'nowrap',
                  }}
                  title="Record id — quote this when reporting a problem with this contact"
                >
                  ID {data.id}
                </span>
                <Badge text={contactType.label} color={contactType.color} />
                {data.pipeline_stage && <Badge text={labelForStage(data.pipeline_stage, stageMeta.options)} color={STAGE_COLORS[data.pipeline_stage] || '#6C757D'} />}
                {data.organization && <span style={{ fontSize: 12, color: '#6C757D' }}>{data.organization}</span>}
              </div>
              <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                {idsMatch && <NextActionPill
                  contact={data}
                  onPatched={() => { loadDetail(); if (onContactUpdated) onContactUpdated(); }}
                />}
                {idsMatch && <ContactTagPicker
                  contact={data}
                  onPatched={() => { loadDetail(); if (onContactUpdated) onContactUpdated(); }}
                />}
              </div>
            </div>
          </div>

          {/* Contact Info */}
          <div style={{ display: 'flex', gap: 16, fontSize: 12, color: '#374151', flex: '1 1 240px', flexWrap: 'wrap' }}>
            {data.email && <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}><Mail size={13} color="#2E8B98" /> {data.email}</div>}
            {data.phone && <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}><Phone size={13} color="#62BB46" /> {data.phone}</div>}
            {(data.city || data.state) && <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}><MapPin size={13} color="#F26522" /> {[data.city, data.state].filter(Boolean).join(', ')}</div>}
            {data.assigned_to_name && <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}><UserCheck size={13} color="#256F7A" /> {data.assigned_to_name}</div>}
          </div>

          {/* Quick Stats */}
          <div style={{ display: 'flex', gap: 10, flex: '0 0 auto', flexWrap: 'wrap' }}>
            {/* ⇣ LEAD SCORE MOVED BELOW — Card CONTACT-PROFILE-INTAKE item 7.
                PIPELINE / DEALS / TASKS stay: they are counts of what exists on
                the record, not a judgement about it. */}
            {totalOppValue > 0 && (
              <div style={{ background: '#ECFDF5', borderRadius: 10, padding: '8px 14px', textAlign: 'center' }}>
                <div style={{ fontSize: 10, color: '#6C757D', fontWeight: 600 }}>PIPELINE</div>
                <div style={{ fontSize: 16, fontWeight: 700, color: '#4FA038' }}>${totalOppValue.toLocaleString()}</div>
              </div>
            )}
            <div style={{ background: '#EFF6FF', borderRadius: 10, padding: '8px 14px', textAlign: 'center' }}>
              <div style={{ fontSize: 10, color: '#6C757D', fontWeight: 600 }}>DEALS</div>
              <div style={{ fontSize: 16, fontWeight: 700, color: '#2E8B98' }}>{(data.opportunities || []).length}</div>
            </div>
            <div style={{ background: '#FEF3C7', borderRadius: 10, padding: '8px 14px', textAlign: 'center' }}>
              <div style={{ fontSize: 10, color: '#6C757D', fontWeight: 600 }}>TASKS</div>
              <div style={{ fontSize: 16, fontWeight: 700, color: '#D97706' }}>{(data.tasks || []).filter(t => t.status !== 'completed').length}</div>
            </div>
          </div>
        </div>

        {/* ⇣ SCORE BREAKDOWN + AT-RISK FLAGS MOVED BELOW — item 7. */}

        {/* Quick Actions
            ⚠ Call / SMS / Email are WRITES in the sense that matters here: they
            reach `data.phone` / `data.email`, so a mismatched row means
            messaging the wrong patient. They are gated with the rest. */}
        <div style={{ display: 'flex', gap: 8, marginTop: 14, borderTop: '1px solid #E5E7EB', paddingTop: 12 }}>
          {idsMatch && data.phone && (
            <button
              style={{ ...btnSm, fontSize: 11 }}
              title={`Click-to-call ${data.phone} (Twilio rings your phone first, then bridges to contact)`}
              disabled={calling}
              onClick={async () => {
                if (calling) return;
                setCalling(true);
                setCallStatus('Calling your phone… answer to connect.');
                // ⛔ ONE flow for all three Call buttons — src/lib/clickToCall.js.
                const out = await placeClickToCall({
                  contactId: data.id, contactPhone: data.phone,
                  contactName: `${data.first_name || ''} ${data.last_name || ''}`,
                });
                if (!out.cancelled) setCallStatus(out.message);
                else setCallStatus(null);
                setCalling(false);
              }}
            >
              <Phone size={12} color="#62BB46" /> {calling ? 'Calling…' : 'Call'}
            </button>
          )}
          {callStatus && (
            <div
              data-testid="click-to-call-status"
              style={{ flexBasis: '100%', fontSize: 11, color: '#6C757D', marginTop: 4 }}
            >
              {callStatus}
            </div>
          )}
          {idsMatch && data.phone && (
            <button style={{ ...btnSm, fontSize: 11 }} title="Send SMS to this contact"
              onClick={() => {
                if (setShowSendModal) {
                  // New behavior: open the global Send Message modal with the
                  // AI Suggest button. Falls back to navigating to the
                  // Conversations sub-tab if the parent didn't pass props.
                  setSendPrefill && setSendPrefill({ contactId: data.id, toAddress: data.phone });
                  setSendChannel && setSendChannel('sms');
                  setSendResult && setSendResult(null);
                  setShowSendModal(true);
                } else {
                  setTab('conversations');
                }
              }}
            ><MessageSquare size={12} color="#2E8B98" /> SMS</button>
          )}
          {idsMatch && data.email && (
            <button style={{ ...btnSm, fontSize: 11 }} title="Send email to this contact"
              onClick={() => {
                if (setShowSendModal) {
                  setSendPrefill && setSendPrefill({ contactId: data.id, toAddress: data.email });
                  setSendChannel && setSendChannel('email');
                  setSendResult && setSendResult(null);
                  setShowSendModal(true);
                } else {
                  window.open(`mailto:${data.email}`);
                }
              }}
            ><Mail size={12} color="#F26522" /> Email</button>
          )}
          {/* ⚠ These two are NAVIGATION, not writes — they only setTab, so
              neither ever produced an ACCESS_DENIED itself. That makes them a
              smaller defect than the NotesPane one below, and the distinction is
              worth keeping straight. But a button labelled "Add Note" that lands
              a reader on a pane with no input (now that NotesPane gates
              correctly) is a dead end wearing an action's clothes. Gated for
              coherence, not for security. */}
          {canWrite && (
            <>
              <button style={{ ...btnSm, fontSize: 11 }} onClick={() => setTab('notes')}><FileText size={12} color="#256F7A" /> Add Note</button>
              <button
                style={{ ...btnSm, fontSize: 11 }}
                title="Add a task for this contact"
                onClick={() => { setTab('tasks'); setShowAddTask(true); }}
              ><Plus size={12} /> Add Task</button>
            </>
          )}
          <div style={{ flex: 1 }} />
          <button onClick={loadDetail} style={btnSm}><RefreshCw size={12} /></button>
        </div>
      </div>

      {/* ═══ MAIN LAYOUT: Tabs + Side Panel ═══ */}
      <div style={{ display: 'flex', gap: 16 }}>
        {/* Left: Tabs Content */}
        <div style={{ flex: 1, minWidth: 0 }}>
          {/* Tab Bar — grouped dropdown nav (same pattern as the CRM main
              nav's 7 categories). The flat 15-tab row had outgrown the
              viewport (Daniel, 2026-06-10): Profile + Team stay standalone,
              everything else folds into Care / Engagement / Business
              dropdowns. Tab IDs are unchanged so deep links + persisted
              state keep working; counts are folded into the sub-tab labels;
              the physician:read gate still applies. */}
          <div style={{ marginBottom: 12 }}>
            <GroupedTabNav
              groups={(() => {
                const counts = {
                  conversations: (data.interactions || []).length,
                  appointments: (data.appointments || []).length,
                  opportunities: (data.opportunities || []).length,
                  activities: (data.timeline || []).length,
                  tasks: (data.tasks || []).length,
                  workflows: (data.workflowRuns || []).length,
                };
                const withCount = (t) =>
                  counts[t.id] > 0 ? { ...t, label: `${t.label} (${counts[t.id]})` } : t;
                const allowed = (t) =>
                  !t.requiresPermission || userPermissions.includes(t.requiresPermission);
                const byId = Object.fromEntries(TABS.map((t) => [t.id, t]));
                const sub = (ids) => ids.map((id) => byId[id]).filter(Boolean).filter(allowed).map(withCount);
                return [
                  { id: 'profile', label: 'Profile', icon: byId.profile.icon },
                  {
                    id: 'care_group', label: 'Care', icon: byId.patient_journey.icon,
                    tabs: sub(['patient_journey', 'discovery', 'physician', 'treatments', 'clinical_ai']),
                  },
                  {
                    id: 'engagement_group', label: 'Engagement', icon: byId.conversations.icon,
                    tabs: sub(['conversations', 'calls', 'appointments', 'activities', 'notes', 'tasks']),
                  },
                  {
                    id: 'business_group', label: 'Business', icon: byId.opportunities.icon,
                    tabs: sub(['opportunities', 'payments', 'workflows']),
                  },
                  { id: 'team', label: 'Team', icon: byId.team.icon },
                ];
              })()}
              activeTab={tab}
              onTabChange={setTab}
              neuShadow={neuShadow}
              neuShadowInset={neuShadowInset}
            />
          </div>

          {/* Tab Content
              ⚠ ContactFieldsPanel, DiscoveryCallPanel, LeadIntakePanel,
              TeamPane and ContactPEMTab expose no write-permission prop — they
              autosave or post directly. There is no flag to pass them, so while
              the identity is unsettled the tab area shows the reason instead.
              The header above still renders, so #373's record id stays on
              screen: the anomaly is visible, and nothing can act on it. */}
          <div style={cardWrap}>
            {!idsMatch ? (
              <div data-testid="contact-tabs-locked" style={{ padding: 24, color: '#78350F', fontSize: 13 }}>
                {data?.id == null
                  ? 'Loading the selected contact…'
                  : "This record doesn't match the one you opened — refresh to try again."}
              </div>
            ) : (<>
            {tab === 'profile' && (
              <>
                {/* ═══ PATIENT INTAKE — FIRST ON THE PROFILE TAB ═══
                    Card CONTACT-PROFILE-INTAKE items 7/8/9. The basic patient
                    fields MOVED here from the right-hand Contact Info card
                    (saving per-field through `crm:contacts:patch-intake`,
                    NOT the full-row `crm:contacts:update`), and the Discovery
                    Call questions are the SAME component Care > Discovery
                    renders — not a second copy of the form.

                    ⛔⛔ RENDERED ONLY WHEN THE DATA IS THE CONTACT IN THE URL —
                    review round 4, blocking item (a).

                    The panel is keyed by contactId, but `data` lags: a switch
                    changes contactId immediately while `data` still holds the
                    PREVIOUS patient until loadDetail resolves. So the fresh
                    "B" instance mounted holding A's row — showing A's values
                    under B's heading, and writing a rep's typing to A.

                    ⚠ The gate is on `data.id`, the LOADED payload, not on a
                    loading flag: a flag says a request is outstanding, which
                    is not the same claim as "what is on screen belongs to this
                    record".

                    ⚠ Only this section waits. The header keeps rendering the
                    old row for an instant rather than blanking, which is what
                    #606 is about — but nothing EDITABLE appears until the
                    identity is settled. */}
                {String(data?.id ?? '') === String(contactId) ? (
                <PatientIntakeSection
                  /*
                    ⛔⛔ KEYED BY CONTACT ID — PR #618 review round 3.

                    ContactDetailView itself is mounted WITHOUT a key (CRMPage
                    and ContactDrawer both), so it survives a contact switch and
                    so did this panel: its refs, its debounce timers and its
                    per-field promise chains carried across from one patient to
                    the next. A write queued while A was open could run after
                    the switch and land on B.

                    ⚠ The key is on THIS component rather than on
                    ContactDetailView because the hazard is this panel's
                    per-field write machinery, and keying the whole detail view
                    would also reset the open tab on every switch — a different,
                    unasked-for behaviour change to a screen #606 has already
                    tuned. Scope the remount to the thing that holds the state.

                    ⇒ A switch is now an unmount (A flushes A's snapshot through
                    A's own chain) followed by a fresh mount for B. The
                    cross-patient write is not guarded against; it is
                    unrepresentable.
                  */
                  key={contactId}
                  mountContactId={contactId}
                  contact={data}
                  canUpdate={canWrite}
                  onPatched={() => { loadDetail(); if (onContactUpdated) onContactUpdated(); }}
                />
                ) : (
                  <div style={{
                    background: '#FFFFFF', border: '1px solid #CBD5E1', borderRadius: 10,
                    marginBottom: 14, padding: '18px 16px', color: '#64748B', fontSize: 13,
                  }}>
                    Loading patient intake…
                  </div>
                )}
                <ContactFieldsPanel
                  contact={data}
                  onPatched={() => { loadDetail(); if (onContactUpdated) onContactUpdated(); }}
                />
              </>
            )}
            {tab === 'patient_journey' && (
              <>
                <ContactPEMTab
                  contactId={contactId}
                  contactData={data}
                  canUpdate={canWrite}
                  onReload={loadDetail}
                />
                {/* Phase 5i — lead intake uploads. Self-gates on the
                    viewer's permissions: standard PEMs see send/revoke
                    only; documents stay physician + senior-PEM. */}
                <LeadIntakePanel contactId={contactId} user={user} />
              </>
            )}
            {tab === 'discovery' && (
              <DiscoveryCallPanel contactId={contactId} user={user} />
            )}
            {tab === 'physician' && canSeePhysician && (
              <>
                <ContactPhysicianTab
                  contactId={contactId}
                  contactData={data}
                  /* Card P100b item 3 — the SAME control as the header, from
                     the same state. A second copy of "is this linked" is how
                     two surfaces start disagreeing. */
                  patientLink={patientLink}
                  onPatientLinkChanged={setPatientLink}
                  canCreatePatient={canCreatePatient}
                  canLinkPatient={canLinkPatient}
                  canUnlinkPatient={canUnlinkPatient}
                  onOpenPatient={onOpenPatient}
                  canWrite={canWritePhysician && idsMatch}
                  canSign={canSignPhysician && idsMatch}
                  canReadProposal={canReadProposal}
                  canWriteProposal={canWriteProposal}
                  canSignProposal={canSignProposal}
                  canWriteProposalDoc={canWriteProposalDoc}
                />
                {/* Phase 5i — pre-consult uploads reviewed during candidacy. */}
                <LeadIntakePanel contactId={contactId} user={user} />
                {/* R2 Phase 1 — administered cell counts/doses + lot QC certs. */}
                <TreatmentAdminPanel contactId={contactId} contactData={data} user={user} />
              </>
            )}
            {tab === 'conversations' && <ConversationsPane interactions={data.interactions || []} contactName={fullName} />}
            {tab === 'calls' && (
              <CallSurfaceTab contactId={contactId} contactData={data} canUpdate={canWrite} onReload={loadDetail} />
            )}
            {tab === 'appointments' && <AppointmentsPane appointments={data.appointments || []} />}
            {tab === 'opportunities' && <OpportunitiesPane opportunities={data.opportunities || []} />}
            {tab === 'treatments' && <TreatmentsPane contactId={contactId} canUpdate={canWrite} />}
            {tab === 'clinical_ai' && (
              <ClinicalAITab
                contactId={contactId}
                contactName={fullName}
              />
            )}
            {tab === 'team' && <TeamPane contactId={contactId} allUsers={allUsers || []} />}
            {tab === 'activities' && <ActivitiesPane timeline={data.timeline || []} interactions={data.interactions || []} />}
            {tab === 'tasks' && (
              <TasksPane
                tasks={data.tasks || []}
                onReload={loadDetail}
                canUpdate={canWrite}
                onAddTask={canWrite ? () => setShowAddTask(true) : null}
              />
            )}
            {tab === 'notes' && <NotesPane contactId={contactId} notes={data.notes} onReload={loadDetail} canUpdate={canWrite} onDirtyChange={setNoteDirty} />}
            {tab === 'workflows' && <WorkflowsPane workflowRuns={data.workflowRuns || []} />}
            {tab === 'payments' && <PaymentsPane />}
            </>)}
          </div>
        </div>

        {/* ═══ 9. RIGHT PANEL: Tags + Custom Fields ═══ */}
        <div style={{ width: 260, flexShrink: 0 }}>
          {/* Tags */}
          <div style={{ ...cardWrap, padding: 14, marginBottom: 12 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#374151', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 4 }}>
              <Tag size={12} color="#2E8B98" /> Tags
            </div>
            {(data.contactTags || []).length > 0 ? (
              <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                {data.contactTags.map((t, i) => (
                  <span key={i} style={{ background: `${t.tag_color || '#2E8B98'}18`, color: t.tag_color || '#2E8B98',
                    padding: '3px 10px', borderRadius: 10, fontSize: 11, fontWeight: 600 }}>{t.tag_name}</span>
                ))}
              </div>
            ) : <div style={{ fontSize: 11, color: '#ADB5BD' }}>No tags</div>}
          </div>

          {/* Contact Info — editable when canUpdate */}
          <div style={{ ...cardWrap, padding: 14, marginBottom: 12 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#374151', marginBottom: 8, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <User size={12} color="#2E8B98" /> Contact Info
              </span>
              {/* ⛔⛔ THE EDITOR THAT USED TO LIVE HERE IS GONE, AND IT NEVER
                  WORKED — Card CONTACT-PROFILE-INTAKE.

                  `startEditing` built editForm in snake_case; crm:contacts:update
                  reads camelCase. first_name / last_name / contact_type arrived
                  undefined, all three are NOT NULL, so every Save died on the
                  constraint and alerted "Save failed". It was also a FULL-ROW
                  update sent a 9-key subset — had the names matched, the first
                  successful save would have NULLed dob, lead_source,
                  medical_interest, next_follow_up_date, pipeline_stage,
                  assigned_to, tags, title, fax, address, zip and
                  referral_source.

                  ⚠ Deleting rather than fixing in place is deliberate: Carlos's
                  item 7 moves these fields to the Patient intake section, and
                  two editors for one row is how the two drift apart. This card
                  keeps the READ-ONLY view (identity reference from any tab) and
                  points at the one editor. */}
              {canWrite && (
                <button
                  onClick={() => setTab('profile')}
                  style={{ ...btnSm, padding: '3px 8px' }}
                  title="Edit these fields in the Patient intake section on the Profile tab"
                >
                  <Edit2 size={11} /> Edit in intake
                </button>
              )}
            </div>
            {(
              <>
                <InfoRow label="Type" value={contactType.label} />
                <InfoRow label="Email" value={data.email} />
                <InfoRow label="Phone" value={data.phone} />
                <InfoRow label="Organization" value={data.organization} />
                <InfoRow label="Title" value={data.title} />
                <InfoRow label="City" value={data.city} />
                <InfoRow label="State" value={data.state} />
                {/* Foundation #1: lead lifecycle fields */}
                <InfoRow label="Date of Birth" value={data.dob ? new Date(data.dob).toLocaleDateString() : null} />
                <InfoRow label="Lead Source" value={data.lead_source} />
                <InfoRow label="Medical Interest" value={data.medical_interest} />
                <InfoRow label="Last Contact" value={data.last_contact_date ? new Date(data.last_contact_date).toLocaleDateString() : null} />
                <InfoRow label="Next Follow-Up" value={data.next_follow_up_date ? new Date(data.next_follow_up_date).toLocaleString() : null} />
                <InfoRow label="Source" value={data.referral_source?.replace(/_/g, ' ')} />
                <OwnerRow
                  contactId={contactId}
                  ownerName={data.assigned_to_name}
                  ownerId={data.assigned_to}
                  canReassign={canReassign && idsMatch}
                  onReassigned={() => { loadDetail(); if (onContactUpdated) onContactUpdated(); }}
                />
                <InfoRow label="Created" value={data.created_at ? new Date(data.created_at).toLocaleDateString() : null} />
              </>
            )}
          </div>

          {/* Custom Fields */}
          {(data.customValues || []).length > 0 && (
            <div style={{ ...cardWrap, padding: 14, marginBottom: 12 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: '#374151', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 4 }}>
                <Briefcase size={12} color="#2E8B98" /> Custom Fields
              </div>
              {data.customValues.map((cv, i) => (
                <InfoRow key={i} label={cv.field_label || cv.field_name} value={cv.value} />
              ))}
            </div>
          )}

          {/* Pipeline Stage */}
          <div style={{ ...cardWrap, padding: 14 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#374151', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 4 }}>
              <Target size={12} color="#2E8B98" /> Pipeline
            </div>
            {/*
              ⛔⛔ Card P116b — THE STAGE IS NOW EDITABLE FROM THE RECORD, and
              the options are the contact's OWN pipeline's stages.

              Maria could see a stage in GHL she could not choose here. The
              pipeline board could already set it (it reads crm_pipeline_stages
              via crm:pipelines:board-stages); the contact record offered no
              control at all, so from this screen the stage was unreachable
              whatever it was called.

              ⚠⚠ MOVING A STAGE LATCHES THE CONTACT. crm:contacts:move-stage
              (routes/crm.js) writes last_modified_source='zignagenix' with no
              app.ghl_import GUC, so mig-162's ratchet stamps zgx_touched_at,
              and BOTH nightly inbound UPDATEs carry `AND zgx_touched_at IS
              NULL` — this contact stops receiving GHL field refreshes, for
              good. That is the SAME consequence the pipeline board's drag has
              always had, so this is not a new hazard, but it is now reachable
              from one more place and the rep is told so in the control's own
              title rather than in a card nobody reads. Narrowing the latch for
              stage moves is a ruling for Daniel (P116), not a thing to decide
              inside a picker.
            */}
            {canWrite ? (
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '4px 0', borderBottom: '1px solid #F3F4F6', gap: 8 }}>
                <span style={{ fontSize: 10, color: '#ADB5BD', fontWeight: 600, textTransform: 'uppercase' }}>Stage</span>
                <select
                  value={data.pipeline_stage || ''}
                  disabled={savingStage}
                  onChange={(e) => changeStage(e.target.value)}
                  title={'Changing the stage here marks this contact as locally edited, '
                       + 'which stops the nightly GoHighLevel sync from refreshing its fields.'}
                  style={{ fontSize: 12, padding: '2px 6px', border: '1px solid #CBD5E1', borderRadius: 6, maxWidth: 210 }}
                >
                  {!data.pipeline_stage && <option value="">— not set —</option>}
                  {stageMeta.options.map((o) => (
                    <option key={o.value} value={o.value} disabled={o.isUnlisted}>
                      {o.label}{o.isUnlisted ? ' (not in this pipeline)' : ''}
                    </option>
                  ))}
                </select>
              </div>
            ) : (
              <InfoRow label="Stage" value={labelForStage(data.pipeline_stage, stageMeta.options)} />
            )}
            {/*
              Which pipeline those stages came from. Without this the list is
              unexplained — a rep seeing stages they do not recognise has no
              way to tell that the contact's DEAL is on another board.
            */}
            {stageMeta.source === 'pipeline' && stageMeta.pipelineName && (
              <InfoRow label="Pipeline" value={stageMeta.pipelineName} />
            )}
            {stageMeta.source === 'legacy' && (
              <div style={{ fontSize: 10, color: '#94A3B8', padding: '4px 0' }}>
                No opportunity yet — showing the default stage list.
              </div>
            )}
            {stageError && (
              <div style={{ fontSize: 10, color: '#DC2626', padding: '4px 0' }}>✗ {stageError}</div>
            )}
            {/* Foundation #2: days-in-stage. stage_entered_at comes from
                migration 037's BEFORE UPDATE trigger. Color cue: green ≤ 7d,
                amber 8-21d, red > 21d to flag stuck leads at a glance. */}
            {data.stage_entered_at && (() => {
              const days = Math.floor((Date.now() - new Date(data.stage_entered_at).getTime()) / 86400000);
              const color = days <= 7 ? '#15803D' : days <= 21 ? '#B45309' : '#DC2626';
              return (
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', borderBottom: '1px solid #F3F4F6' }}>
                  <span style={{ fontSize: 10, color: '#ADB5BD', fontWeight: 600, textTransform: 'uppercase' }}>Days in Stage</span>
                  <span style={{ fontSize: 12, color, fontWeight: 700 }}>{days} {days === 1 ? 'day' : 'days'}</span>
                </div>
              );
            })()}
            <InfoRow label="Referral Source" value={data.referral_source?.replace(/_/g, ' ')} />
            <InfoRow label="Fax" value={data.fax} />
          </div>
        </div>
      </div>

      {/* ═══ LEAD HEALTH + REP ACCOUNTABILITY — BELOW THE INTAKE ═══
          Card CONTACT-PROFILE-INTAKE item 7. These three were above the fold:
          Rep Accountability at the very top, the lead score in the header's
          Quick Stats, the breakdown and at-risk flags under it. Carlos asked
          for the intake form first and these below it. Same components, same
          data, same conditions — moved, not reworked. */}
      {typeof data._leadScore === 'number' && (
        <div style={{ ...cardWrap, padding: '14px 18px', marginTop: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
            <div style={{
              background: data._leadBand === 'hot' ? '#DCFCE7' : data._leadBand === 'active' ? '#FEF3C7' : '#FEE2E2',
              borderRadius: 10,
              padding: '8px 14px',
              textAlign: 'center',
              minWidth: 110,
              outline: data._isAtRisk ? '2px solid #FBBF24' : 'none',
              outlineOffset: 1,
            }}>
              <div style={{ fontSize: 10, color: '#6C757D', fontWeight: 600 }}>LEAD SCORE</div>
              <div style={{
                fontSize: 22,
                fontWeight: 700,
                color: data._leadBand === 'hot' ? '#15803D' : data._leadBand === 'active' ? '#B45309' : '#DC2626',
                lineHeight: '24px',
              }}>{data._leadScore}<span style={{ fontSize: 11, fontWeight: 500, color: '#6C757D' }}>/100</span></div>
              <div style={{
                fontSize: 10,
                fontWeight: 700,
                textTransform: 'uppercase',
                color: data._leadBand === 'hot' ? '#15803D' : data._leadBand === 'active' ? '#B45309' : '#DC2626',
                marginTop: 2,
              }}>
                {data._leadBand === 'hot' ? '🟢 Hot' : data._leadBand === 'active' ? '🟡 Active' : '🔴 At Risk'}
              </div>
            </div>
            <div style={{ flex: 1, minWidth: 240 }}>
          {/* Lead Score Breakdown (collapsible, shown when score present) */}
          {typeof data._leadScore === 'number' && data._scoreCategories && (
            <div style={{ marginTop: 12, padding: '10px 14px', background: '#F8FAFC', borderRadius: 8, border: '1px solid #E5E7EB' }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: '#475569', marginBottom: 8, letterSpacing: 0.5 }}>SCORE BREAKDOWN</div>
              <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', fontSize: 11 }}>
                <ScoreBar label="Engagement" value={data._scoreCategories.engagement?.points} max={30} color="#0369A1" />
                <ScoreBar label="Recency" value={data._scoreCategories.recency?.points} max={25} color="#7C3AED" />
                <ScoreBar label="Progress" value={data._scoreCategories.progress?.points} max={25} color="#2E8B98" />
                <ScoreBar label="Value" value={data._scoreCategories.value?.points} max={20} color="#15803D" />
              </div>
              {data._isAtRisk && (
                <div style={{ marginTop: 10, padding: '8px 10px', background: '#FEF3C7', borderRadius: 6, border: '1px solid #FBBF24' }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: '#B45309', marginBottom: 4 }}>⚠ AT RISK FLAGS</div>
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    {(data._atRiskFlags || []).map((flag) => (
                      <span key={flag} style={{ background: '#FBBF24', color: '#78350F', padding: '2px 8px', borderRadius: 999, fontSize: 10, fontWeight: 600 }}>
                        {flag === 'no_contact_72h' ? 'No contact 72h+'
                          : flag === 'stuck_in_stage_5d' ? 'Stuck in stage 5d+'
                          : flag === 'high_value_no_response' ? 'High value, no response'
                          : flag === 'missed_consult' ? 'Missed scheduled consult'
                          : flag}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
            </div>
          </div>
        </div>
      )}

      <AccountabilityPanel contactId={contactId} />

      {/* ═══ ADD TASK — item 12 ═══
          ⚠ `onCreated` reloads the CONTACT detail, which is where this record's
          task list comes from (getCRMContactFullDetail -> data.tasks). That is
          the same contract the Tasks tab needs and gets a different way: each
          caller must refresh the list the user is actually looking at. */}
      {showAddTask && (
        <AddTaskModal
          contacts={data ? [data] : []}
          users={allUsers || []}
          prefillContactId={contactId}
          lockContact
          onClose={() => setShowAddTask(false)}
          onCreated={() => {
            setShowAddTask(false);
            loadDetail();
            if (onContactUpdated) onContactUpdated();
          }}
        />
      )}
    </div>
  );
}

// ─── Lead Score progress bar (one per category) ─────────────────────────────

function ScoreBar({ label, value, max, color }) {
  const v = typeof value === 'number' ? value : 0;
  const pct = max > 0 ? Math.max(0, Math.min(100, (v / max) * 100)) : 0;
  return (
    <div style={{ flex: '1 1 140px', minWidth: 140 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
        <span style={{ fontSize: 11, color: '#374151', fontWeight: 600 }}>{label}</span>
        <span style={{ fontSize: 11, color: '#6C757D' }}>{v}/{max}</span>
      </div>
      <div style={{ background: '#E5E7EB', borderRadius: 999, height: 6, overflow: 'hidden' }}>
        <div style={{ width: `${pct}%`, height: '100%', background: color, transition: 'width 0.3s' }} />
      </div>
    </div>
  );
}

// ─── Info Row (side panel) ───────────────────────────────────────────────────

function InfoRow({ label, value }) {
  if (!value) return null;
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', borderBottom: '1px solid #F3F4F6' }}>
      <span style={{ fontSize: 10, color: '#ADB5BD', fontWeight: 600, textTransform: 'uppercase' }}>{label}</span>
      <span style={{ fontSize: 11, color: '#374151', textAlign: 'right', maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{value}</span>
    </div>
  );
}

// ─── Owner Row (#136: inline owner reassignment) ────────────────────────────
// Read-only owner display, plus an "Edit Owner" affordance when the viewer
// holds crm:reassign. Picking a new owner + Save calls crm:contacts:reassign-owner
// and refreshes the parent so the new owner shows in the profile, the sidebar,
// and (via the opportunity owner_id cascade) the pipeline card.
function OwnerRow({ contactId, ownerName, ownerId, canReassign, onReassigned }) {
  const [editing, setEditing] = useState(false);
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [selected, setSelected] = useState(ownerId ? String(ownerId) : '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [activity, setActivity] = useState(null);

  const startEdit = async () => {
    setError(null);
    setSelected(ownerId ? String(ownerId) : '');
    setEditing(true);
    setLoadingUsers(true);
    try {
      const rows = await window.zignagenixAPI.getCRMAssignableUsers();
      if (Array.isArray(rows)) setUsers(rows);
      else { setUsers([]); setError(rows?.error || 'Could not load users'); }
    } catch (e) {
      setError(e?.message || 'Could not load users');
    } finally {
      setLoadingUsers(false);
    }
  };

  const cancel = () => { setEditing(false); setError(null); };

  const save = async () => {
    if (!selected) { setError('Select an owner'); return; }
    setSaving(true);
    setError(null);
    try {
      const result = await window.zignagenixAPI.reassignCRMContactOwner(contactId, Number(selected));
      if (!result || result.error || result.success === false) {
        setError(result?.error || result?.message || 'Reassignment failed');
      } else {
        setActivity(result.description || 'Owner reassigned');
        setEditing(false);
        if (onReassigned) onReassigned();
      }
    } catch (e) {
      setError(e?.message || 'Reassignment failed');
    } finally {
      setSaving(false);
    }
  };

  if (!editing) {
    return (
      <>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '4px 0', borderBottom: '1px solid #F3F4F6' }}>
          <span style={{ fontSize: 10, color: '#ADB5BD', fontWeight: 600, textTransform: 'uppercase' }}>Owner</span>
          <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ fontSize: 11, color: '#374151', textAlign: 'right', maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {ownerName || 'Unassigned'}
            </span>
            {canReassign && (
              <button onClick={startEdit} style={{ ...btnSm, padding: '2px 6px' }} title="Reassign owner" aria-label="Edit Owner">
                <Edit2 size={10} />
              </button>
            )}
          </span>
        </div>
        {activity && (
          <div style={{ fontSize: 10, color: '#15803D', padding: '4px 0', borderBottom: '1px solid #F3F4F6' }}>
            {activity}
          </div>
        )}
      </>
    );
  }

  return (
    <div style={{ padding: '6px 0', borderBottom: '1px solid #F3F4F6' }}>
      <div style={{ fontSize: 10, color: '#ADB5BD', fontWeight: 600, textTransform: 'uppercase', marginBottom: 4 }}>Owner</div>
      <select
        value={selected}
        onChange={(e) => setSelected(e.target.value)}
        disabled={loadingUsers || saving}
        aria-label="Select owner"
        style={{ ...inp, fontSize: 12, padding: '4px 6px', width: '100%', marginBottom: 6 }}
      >
        <option value="">{loadingUsers ? 'Loading…' : 'Select owner…'}</option>
        {users.map((u) => (
          <option key={u.id} value={String(u.id)}>
            {u.display_name}{u.role ? ` (${u.role})` : ''}
          </option>
        ))}
      </select>
      {error && <div style={{ fontSize: 10, color: '#DC3545', marginBottom: 6 }}>{error}</div>}
      <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
        <button onClick={save} disabled={saving || loadingUsers} style={{ ...btnSm, padding: '3px 10px', color: '#4FA038' }} title="Save new owner">
          {saving ? '…' : 'Save'}
        </button>
        <button onClick={cancel} disabled={saving} style={{ ...btnSm, padding: '3px 8px', color: '#DC3545' }} title="Cancel">
          <X size={11} />
        </button>
      </div>
    </div>
  );
}

// ─── Edit Field Row (inline edit mode for the Contact Info card) ─────────────


// ─── 2. Conversations Pane ───────────────────────────────────────────────────

function ConversationsPane({ interactions, contactName }) {
  const typeIcons = { phone_call: Phone, email: Mail, text_message: MessageSquare, sms: MessageSquare, video_call: Globe, in_person: User };
  const dirColors = { inbound: '#62BB46', outbound: '#2E8B98' };

  // Selection: when a row is clicked, expand it inline to show the full body,
  // outcome, attachments, recording, and any other metadata. Click again to collapse.
  const [selectedId, setSelectedId] = useState(null);
  const [search, setSearch] = useState('');

  if (interactions.length === 0) return <div style={{ ...emptyMsg, padding: 30 }}>No conversations yet. Start one above!</div>;

  // Sort newest first
  const sorted = [...interactions].sort((a, b) => {
    const ta = a.interaction_date ? new Date(a.interaction_date).getTime() : 0;
    const tb = b.interaction_date ? new Date(b.interaction_date).getTime() : 0;
    return tb - ta;
  });

  // Apply search filter on subject/description/outcome
  const filtered = search.trim()
    ? sorted.filter((ix) => {
        const q = search.toLowerCase();
        return (
          (ix.subject || '').toLowerCase().includes(q) ||
          (ix.description || '').toLowerCase().includes(q) ||
          (ix.outcome || '').toLowerCase().includes(q) ||
          (ix.interaction_type || '').toLowerCase().includes(q)
        );
      })
    : sorted;

  return (
    <div>
      {/* Search bar + count */}
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 10, padding: '0 4px' }}>
        <div style={{ position: 'relative', flex: 1 }}>
          <input
            placeholder={`Search ${interactions.length} conversation${interactions.length !== 1 ? 's' : ''}...`}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{
              width: '100%', padding: '6px 10px', borderRadius: 8, border: '1px solid #E5E7EB',
              background: '#fff', fontSize: 12, outline: 'none',
            }}
          />
        </div>
        <span style={{ fontSize: 11, color: '#6C757D' }}>
          {filtered.length} of {interactions.length}
        </span>
        {selectedId && (
          <button
            onClick={() => setSelectedId(null)}
            style={{
              fontSize: 11, padding: '4px 10px', borderRadius: 999, border: '1px solid #E5E7EB',
              background: '#fff', color: '#475569', cursor: 'pointer', fontWeight: 600,
            }}
          >
            Collapse
          </button>
        )}
      </div>

      <div style={{ maxHeight: 500, overflowY: 'auto', borderRadius: 8, border: '1px solid #F3F4F6' }}>
        {filtered.length === 0 ? (
          <div style={{ ...emptyMsg, padding: 30 }}>No conversations match your search.</div>
        ) : filtered.map((ix, i) => {
          const Icon = typeIcons[ix.interaction_type] || Activity;
          const isSelected = ix.id === selectedId;
          return (
            <div
              key={ix.id || i}
              onClick={() => setSelectedId(isSelected ? null : ix.id)}
              style={{
                padding: '12px 16px',
                borderBottom: i === filtered.length - 1 ? 'none' : '1px solid #F3F4F6',
                display: 'flex',
                gap: 12,
                alignItems: 'flex-start',
                cursor: 'pointer',
                background: isSelected ? '#F1F5F9' : 'transparent',
                borderLeft: isSelected ? `3px solid ${dirColors[ix.direction] || '#6C757D'}` : '3px solid transparent',
                transition: 'background 0.1s',
              }}
            >
              <div style={{ width: 32, height: 32, borderRadius: 8,
                background: `${dirColors[ix.direction] || '#6C757D'}18`,
                display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <Icon size={15} color={dirColors[ix.direction] || '#6C757D'} />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: '#212529', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: isSelected ? 'normal' : 'nowrap' }}>
                    {ix.subject || `(${ix.interaction_type?.replace(/_/g, ' ') || 'note'})`}
                  </div>
                  <span style={{ fontSize: 10, color: '#ADB5BD', flexShrink: 0 }}>
                    {ix.interaction_date ? new Date(ix.interaction_date).toLocaleString('en-US', {
                      month: 'short', day: 'numeric', year: 'numeric',
                      hour: 'numeric', minute: '2-digit'
                    }) : ''}
                  </span>
                </div>
                <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginTop: 4, flexWrap: 'wrap' }}>
                  <Badge text={ix.direction || 'outbound'} color={dirColors[ix.direction] || '#6C757D'} />
                  <Badge text={ix.interaction_type?.replace(/_/g, ' ') || 'other'} color="#6C757D" />
                  {ix.duration_minutes && <span style={{ fontSize: 10, color: '#ADB5BD' }}>{ix.duration_minutes}min</span>}
                  {ix.logged_by_name && <span style={{ fontSize: 10, color: '#ADB5BD' }}>· {ix.logged_by_name}</span>}
                  <span style={{ fontSize: 10, color: '#94A3B8', marginLeft: 'auto' }}>
                    {isSelected ? '▴ collapse' : '▾ click to expand'}
                  </span>
                </div>

                {/* Default state is fully collapsed: subject + date + badges only.
                    No body teaser. Click the row to expand and see body. */}

                {/* Expanded: show full details */}
                {isSelected && (
                  <div style={{ marginTop: 10, paddingTop: 10, borderTop: '1px solid #E5E7EB' }}>
                    {ix.description && (
                      <div style={{ marginBottom: 10 }}>
                        <div style={{ fontSize: 10, color: '#94A3B8', fontWeight: 600, textTransform: 'uppercase', marginBottom: 4 }}>Body</div>
                        <div style={{
                          fontSize: 12, color: '#374151', lineHeight: 1.5,
                          background: '#fff', padding: 10, borderRadius: 6, border: '1px solid #E5E7EB',
                          whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                        }}>
                          {ix.description}
                        </div>
                      </div>
                    )}
                    {ix.outcome && (
                      <div style={{ marginBottom: 10 }}>
                        <div style={{ fontSize: 10, color: '#94A3B8', fontWeight: 600, textTransform: 'uppercase', marginBottom: 4 }}>Outcome</div>
                        <div style={{ fontSize: 12, color: '#374151' }}>{ix.outcome}</div>
                      </div>
                    )}
                    {ix.recording_url && (
                      <div style={{ marginBottom: 10 }}>
                        <div style={{ fontSize: 10, color: '#94A3B8', fontWeight: 600, textTransform: 'uppercase', marginBottom: 4 }}>Recording</div>
                        <a href={ix.recording_url} target="_blank" rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          style={{ fontSize: 12, color: '#0369A1', textDecoration: 'underline' }}>
                          Listen to recording →
                        </a>
                      </div>
                    )}
                    {ix.transcript && (
                      <div style={{ marginBottom: 10 }}>
                        <div style={{ fontSize: 10, color: '#94A3B8', fontWeight: 600, textTransform: 'uppercase', marginBottom: 4 }}>Transcript</div>
                        <div style={{
                          fontSize: 11, color: '#475569', lineHeight: 1.5,
                          background: '#F8FAFC', padding: 10, borderRadius: 6, border: '1px solid #E5E7EB',
                          whiteSpace: 'pre-wrap', wordBreak: 'break-word', maxHeight: 240, overflowY: 'auto',
                        }}>
                          {ix.transcript}
                        </div>
                      </div>
                    )}
                    <div style={{ display: 'flex', gap: 12, fontSize: 10, color: '#94A3B8', marginTop: 8 }}>
                      <span>ID #{ix.id}</span>
                      {ix.channel && <span>· Channel: {ix.channel}</span>}
                      {ix.thread_id && <span>· Thread: {ix.thread_id}</span>}
                      {ix.status && <span>· Status: {ix.status}</span>}
                    </div>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── 3. Appointments Pane ────────────────────────────────────────────────────

function AppointmentsPane({ appointments }) {
  if (appointments.length === 0) return <div style={{ ...emptyMsg, padding: 30 }}>No appointments scheduled.</div>;

  return (
    <div style={{ maxHeight: 500, overflowY: 'auto' }}>
      {appointments.map((apt, i) => (
        <div key={apt.id || i} style={{ padding: '12px 16px', borderBottom: '1px solid #F3F4F6', display: 'flex', gap: 12, alignItems: 'flex-start' }}>
          <div style={{ width: 32, height: 32, borderRadius: 8, background: `${APPT_STATUS_COLORS[apt.status] || '#6C757D'}18`,
            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <Calendar size={15} color={APPT_STATUS_COLORS[apt.status] || '#6C757D'} />
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: '#212529' }}>{apt.title}</div>
            <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginTop: 4 }}>
              <Badge text={apt.status || 'scheduled'} color={APPT_STATUS_COLORS[apt.status] || '#6C757D'} />
              <span style={{ fontSize: 11, color: '#6C757D' }}>
                {apt.start_time ? new Date(apt.start_time).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : ''}
              </span>
              {apt.location && <span style={{ fontSize: 11, color: '#ADB5BD' }}>{apt.location}</span>}
            </div>
            {apt.description && <div style={{ fontSize: 12, color: '#6C757D', marginTop: 4 }}>{apt.description}</div>}
          </div>
        </div>
      ))}
    </div>
  );
}

// ─── 4. Opportunities Pane ───────────────────────────────────────────────────

function OpportunitiesPane({ opportunities }) {
  if (opportunities.length === 0) return <div style={{ ...emptyMsg, padding: 30 }}>No opportunities tied to this contact.</div>;

  return (
    <div style={{ maxHeight: 500, overflowY: 'auto' }}>
      {opportunities.map((opp, i) => (
        <div key={opp.id || i} style={{ padding: '14px 16px', borderBottom: '1px solid #F3F4F6', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <div style={{ fontSize: 13, fontWeight: 600, color: '#212529' }}>{opp.title}</div>
            <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginTop: 4 }}>
              {/* ⛔ OPPORTUNITY stage, NOT contact stage — a different vocabulary in a
                  different column (P95c). Deliberately left on the static labels: this
                  card only RENDERS a deal's stage and src/lib/opportunityStages.js is
                  the owner of what a UI may WRITE there. Same map as before this card,
                  moved to the shared module so there is one copy of the eleven codes. */}
              <Badge text={LEGACY_STAGE_LABELS[opp.stage] || opp.stage} color={STAGE_COLORS[opp.stage] || '#6C757D'} />
              <Badge text={OPP_STATUSES[opp.status]?.label || opp.status} color={OPP_STATUSES[opp.status]?.color || '#6C757D'} bg={OPP_STATUSES[opp.status]?.bg} />
              {opp.owner_name && <span style={{ fontSize: 10, color: '#ADB5BD' }}>{opp.owner_name}</span>}
            </div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: 16, fontWeight: 700, color: '#212529' }}>${Number(opp.value || 0).toLocaleString()}</div>
            {opp.probability && <div style={{ fontSize: 10, color: '#ADB5BD' }}>{opp.probability}% probability</div>}
          </div>
        </div>
      ))}
      <div style={{ padding: '10px 16px', fontSize: 12, fontWeight: 600, color: '#2E8B98', borderTop: '2px solid #E5E7EB' }}>
        Total Pipeline: ${opportunities.reduce((s, o) => s + Number(o.value || 0), 0).toLocaleString()}
      </div>
    </div>
  );
}

// ─── 4b. Treatments Pane (medical plans + protocols + outcomes) ────────────

function TreatmentsPane({ contactId, canUpdate }) {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingPlanId, setEditingPlanId] = useState(undefined);  // undefined = closed, null = create new

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const r = await window.zignagenixAPI.listTreatmentPlans({ contactId });
      setPlans(Array.isArray(r) ? r : []);
    } finally { setLoading(false); }
  }, [contactId]);

  useEffect(() => { reload(); }, [reload]);

  async function deletePlan(plan) {
    if (!window.confirm(`Delete plan "${plan.plan_name}"? All protocols and outcomes will be deleted.`)) return;
    await window.zignagenixAPI.deleteTreatmentPlan(plan.id);
    reload();
  }

  return (
    <div style={{ padding: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: '#212529', display: 'flex', alignItems: 'center', gap: 6 }}>
          <Heart size={14} color="#DC2626" /> Treatment Plans
        </div>
        {canUpdate && (
          <button onClick={() => setEditingPlanId(null)} style={btnPrimary}>
            <Plus size={13} /> New Plan
          </button>
        )}
      </div>

      {loading ? (
        <div style={emptyMsg}>Loading…</div>
      ) : plans.length === 0 ? (
        <div style={{ ...emptyMsg, padding: 24 }}>
          No treatment plans for this contact.{canUpdate && ' Add one to start tracking protocols and outcomes.'}
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {plans.map(plan => {
            const cs = PLAN_STATUS_COLORS[plan.status] || PLAN_STATUS_COLORS.draft;
            return (
              <div key={plan.id} style={{
                padding: 12,
                background: '#fff',
                borderRadius: 10,
                boxShadow: neuShadowSm,
                cursor: canUpdate ? 'pointer' : 'default',
              }} onClick={canUpdate ? () => setEditingPlanId(plan.id) : undefined}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: '#212529', flex: 1 }}>
                    {plan.plan_name}
                  </div>
                  <span style={{
                    background: cs.bg, color: cs.color, fontSize: 10, fontWeight: 700,
                    padding: '2px 8px', borderRadius: 999, textTransform: 'uppercase',
                  }}>{plan.status}</span>
                  {canUpdate && (
                    <button onClick={(e) => { e.stopPropagation(); deletePlan(plan); }} style={{
                      background: 'none', border: 'none', cursor: 'pointer', padding: 2,
                    }} title="Delete plan">
                      <Trash2 size={12} color="#DC3545" />
                    </button>
                  )}
                </div>
                {plan.primary_goal && (
                  <div style={{ fontSize: 11, color: '#475569', marginBottom: 4 }}>
                    <Target size={10} style={{ display: 'inline', marginRight: 4 }} />
                    {plan.primary_goal}
                  </div>
                )}
                <div style={{ display: 'flex', gap: 12, fontSize: 10, color: '#6C757D', marginTop: 4 }}>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                    <Pill size={10} /> {plan.protocol_count || 0} protocol{plan.protocol_count == 1 ? '' : 's'}
                  </span>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                    <Heart size={10} /> {plan.outcome_count || 0} outcome{plan.outcome_count == 1 ? '' : 's'}
                  </span>
                  {plan.start_date && (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                      <Calendar size={10} /> Started {new Date(plan.start_date).toLocaleDateString()}
                    </span>
                  )}
                  {plan.created_by_name && (
                    <span style={{ marginLeft: 'auto' }}>by {plan.created_by_name}</span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {editingPlanId !== undefined && (
        <TreatmentPlanModal
          planId={editingPlanId || undefined}
          contactId={contactId}
          onClose={() => setEditingPlanId(undefined)}
          onSaved={() => { setEditingPlanId(undefined); reload(); }}
        />
      )}
    </div>
  );
}

// ─── 4c. Team Pane (internal staff comments + @mentions) ──────────────────

function TeamPane({ contactId, allUsers }) {
  const [comments, setComments] = useState([]);
  const [body, setBody] = useState('');
  const [loading, setLoading] = useState(true);
  const [posting, setPosting] = useState(false);
  const [showMentions, setShowMentions] = useState(false);
  const [mentionFilter, setMentionFilter] = useState('');
  const textareaRef = React.useRef(null);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const r = await window.zignagenixAPI.listContactComments(contactId);
      setComments(Array.isArray(r) ? r : []);
    } finally { setLoading(false); }
  }, [contactId]);

  useEffect(() => { reload(); }, [reload]);

  // Detect @ to show a username picker
  function onBodyChange(e) {
    const v = e.target.value;
    setBody(v);
    const cursor = e.target.selectionStart || v.length;
    const before = v.slice(0, cursor);
    const m = before.match(/@([a-zA-Z0-9._-]*)$/);
    if (m) {
      setShowMentions(true);
      setMentionFilter(m[1].toLowerCase());
    } else {
      setShowMentions(false);
    }
  }

  function insertMention(username) {
    const ta = textareaRef.current;
    if (!ta) return;
    const cursor = ta.selectionStart || body.length;
    const before = body.slice(0, cursor);
    const after = body.slice(cursor);
    const replaced = before.replace(/@([a-zA-Z0-9._-]*)$/, `@${username} `);
    const newBody = replaced + after;
    setBody(newBody);
    setShowMentions(false);
    setTimeout(() => {
      ta.focus();
      ta.setSelectionRange(replaced.length, replaced.length);
    }, 0);
  }

  async function post() {
    if (!body.trim()) return;
    setPosting(true);
    try {
      await window.zignagenixAPI.postContactComment({ contactId, body: body.trim() });
      setBody('');
      reload();
    } catch (err) {
      alert(`Post failed: ${err.message}`);
    } finally { setPosting(false); }
  }

  async function deleteOne(c) {
    if (!window.confirm('Delete this comment?')) return;
    await window.zignagenixAPI.deleteContactComment(c.id);
    reload();
  }

  const filteredUsers = (allUsers || []).filter(u => {
    if (!mentionFilter) return true;
    return (u.username || '').toLowerCase().includes(mentionFilter)
        || (u.display_name || '').toLowerCase().includes(mentionFilter);
  }).slice(0, 6);

  return (
    <div style={{ padding: 16 }}>
      <div style={{ fontSize: 13, fontWeight: 700, color: '#212529', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 6 }}>
        <Users size={14} color="#2E8B98" /> Internal Team Notes
        <span style={{ fontSize: 10, fontWeight: 400, color: '#94A3B8', marginLeft: 'auto' }}>
          Visible only to staff. Use @username to notify a teammate.
        </span>
      </div>

      {/* Composer */}
      <div style={{ position: 'relative', marginBottom: 16 }}>
        <textarea
          ref={textareaRef}
          value={body}
          onChange={onBodyChange}
          onKeyDown={(e) => {
            if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') { e.preventDefault(); post(); }
            if (e.key === 'Escape') setShowMentions(false);
          }}
          placeholder="Note for the team — supports @mentions. Cmd/Ctrl+Enter to post."
          rows={3}
          style={{ ...inp, resize: 'vertical' }}
        />
        {/* Mention picker */}
        {showMentions && filteredUsers.length > 0 && (
          <div style={{
            position: 'absolute', bottom: 'calc(100% + 4px)', left: 0,
            background: '#fff', borderRadius: 8, boxShadow: '0 4px 16px rgba(15,23,42,0.12)',
            zIndex: 10, padding: 4, minWidth: 220, maxHeight: 220, overflowY: 'auto',
          }}>
            {filteredUsers.map(u => (
              <button key={u.id} type="button" onClick={() => insertMention(u.username || u.display_name)}
                style={{
                  display: 'flex', width: '100%', alignItems: 'center', gap: 8,
                  padding: '6px 10px', background: 'none', border: 'none', cursor: 'pointer',
                  borderRadius: 6, fontSize: 12, color: '#212529', textAlign: 'left',
                }}
                onMouseEnter={(e) => e.currentTarget.style.background = '#F1F5F9'}
                onMouseLeave={(e) => e.currentTarget.style.background = 'none'}>
                <AtSign size={11} color="#2E8B98" />
                <span style={{ fontWeight: 600 }}>{u.username}</span>
                <span style={{ color: '#94A3B8', marginLeft: 'auto' }}>{u.display_name}</span>
              </button>
            ))}
          </div>
        )}
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 6 }}>
          <button onClick={post} disabled={posting || !body.trim()} style={btnPrimary}>
            <Send size={12} /> {posting ? 'Posting…' : 'Post'}
          </button>
        </div>
      </div>

      {/* Thread */}
      {loading ? (
        <div style={emptyMsg}>Loading…</div>
      ) : comments.length === 0 ? (
        <div style={{ ...emptyMsg, padding: 24 }}>
          No team notes yet. Drop a quick line for the next person who works this lead.
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {comments.map(c => (
            <div key={c.id} style={{
              padding: 12,
              background: '#fff',
              borderRadius: 10,
              boxShadow: neuShadowSm,
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                <div style={{
                  width: 24, height: 24, borderRadius: 12, background: '#2E8B98',
                  color: '#fff', fontWeight: 700, fontSize: 11,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  {(c.author_name || '?').charAt(0).toUpperCase()}
                </div>
                <div style={{ fontSize: 12, fontWeight: 700, color: '#212529' }}>{c.author_name}</div>
                <div style={{ fontSize: 10, color: '#94A3B8' }}>
                  {new Date(c.created_at).toLocaleString()}
                  {c.edited_at && ' · edited'}
                </div>
                <div style={{ flex: 1 }} />
                <button onClick={() => deleteOne(c)} style={{
                  background: 'none', border: 'none', cursor: 'pointer', padding: 2,
                }} title="Delete">
                  <Trash2 size={11} color="#94A3B8" />
                </button>
              </div>
              <div style={{ fontSize: 13, color: '#212529', whiteSpace: 'pre-wrap', lineHeight: 1.5 }}>
                {renderMentionedBody(c.body)}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// Renders @username mentions as colored chips inline.
function renderMentionedBody(body) {
  if (!body) return '';
  const parts = body.split(/(@[a-zA-Z0-9._-]+)/g);
  return parts.map((p, i) => {
    if (p.startsWith('@')) {
      return <span key={i} style={{
        background: '#DBEAFE', color: '#1D4ED8', padding: '1px 5px',
        borderRadius: 4, fontWeight: 600, fontSize: 12,
      }}>{p}</span>;
    }
    return <React.Fragment key={i}>{p}</React.Fragment>;
  });
}

// ─── 5. Activities Pane ──────────────────────────────────────────────────────

function ActivitiesPane({ timeline, interactions }) {
  // Merge timeline + interactions into unified activity feed
  const activities = [
    ...(timeline || []).map(t => ({ type: t.event_type, title: t.title, desc: t.description, date: t.created_at, source: 'timeline' })),
    ...(interactions || []).map(ix => ({ type: ix.interaction_type, title: ix.subject, desc: ix.description, date: ix.interaction_date, source: 'interaction', direction: ix.direction })),
  ].sort((a, b) => new Date(b.date) - new Date(a.date));

  if (activities.length === 0) return <div style={{ ...emptyMsg, padding: 30 }}>No activity recorded yet.</div>;

  return (
    <div style={{ maxHeight: 500, overflowY: 'auto' }}>
      {activities.map((act, i) => (
        <div key={i} style={{ padding: '10px 16px', borderBottom: '1px solid #F3F4F6', display: 'flex', gap: 10, alignItems: 'flex-start' }}>
          <div style={{ width: 8, height: 8, borderRadius: '50%', background: act.source === 'interaction' ? '#2E8B98' : '#62BB46', marginTop: 6, flexShrink: 0 }} />
          <div style={{ flex: 1 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ fontSize: 12, fontWeight: 600, color: '#212529' }}>{act.title || act.type?.replace(/_/g, ' ')}</span>
              <span style={{ fontSize: 10, color: '#ADB5BD' }}>
                {act.date ? new Date(act.date).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : ''}
              </span>
            </div>
            {act.desc && <div style={{ fontSize: 11, color: '#6C757D', marginTop: 2 }}>{act.desc}</div>}
          </div>
        </div>
      ))}
    </div>
  );
}

// ─── 6. Tasks Pane ───────────────────────────────────────────────────────────

function TasksPane({ tasks, onReload, canUpdate, onAddTask }) {
  const pending = tasks.filter(t => t.status !== 'completed' && t.status !== 'cancelled');
  const done = tasks.filter(t => t.status === 'completed');

  const completeTask = async (id) => {
    try { await window.zignagenixAPI.completeCRMTask(id); onReload(); } catch (e) { alert('Error: ' + e.message); }
  };

  return (
    <div style={{ maxHeight: 500, overflowY: 'auto' }}>
      {/* ⛔ THE CREATE CONTROL THIS PANE NEVER HAD — item 12. Gated on
          canUpdate for the same reason NotesPane is: crm:create refuses
          otherwise, and a button that exists only to return ACCESS_DENIED is
          worse than no button. */}
      {onAddTask && (
        <div style={{ padding: '10px 16px', borderBottom: '1px solid #E5E7EB' }}>
          <button onClick={onAddTask} style={{ ...btnSm, fontSize: 11 }}>
            <Plus size={12} /> Add Task
          </button>
        </div>
      )}
      {pending.length > 0 && (
        <div>
          <div style={{ padding: '10px 16px', fontSize: 11, fontWeight: 700, color: '#374151', background: '#F9FAFB', borderBottom: '1px solid #E5E7EB' }}>
            Open Tasks ({pending.length})
          </div>
          {pending.map(t => (
            <div key={t.id} style={{ padding: '10px 16px', borderBottom: '1px solid #F3F4F6', display: 'flex', alignItems: 'center', gap: 10 }}>
              {canUpdate && (
                <button onClick={() => completeTask(t.id)} style={{ background: 'none', border: '2px solid #D1D5DB', borderRadius: 4, width: 18, height: 18, cursor: 'pointer', padding: 0, flexShrink: 0 }} />
              )}
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13, fontWeight: 500, color: '#212529' }}>{t.title}</div>
                <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginTop: 2 }}>
                  {t.priority && <Badge text={TASK_PRIORITY[t.priority]?.label || t.priority} color={TASK_PRIORITY[t.priority]?.color || '#6C757D'} bg={TASK_PRIORITY[t.priority]?.bg} />}
                  {t.due_date && <span style={{ fontSize: 10, color: new Date(t.due_date) < new Date() ? '#DC3545' : '#ADB5BD' }}>Due {new Date(t.due_date).toLocaleDateString()}</span>}
                  {t.assigned_to_name && <span style={{ fontSize: 10, color: '#ADB5BD' }}>{t.assigned_to_name}</span>}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
      {done.length > 0 && (
        <div>
          <div style={{ padding: '10px 16px', fontSize: 11, fontWeight: 700, color: '#374151', background: '#F9FAFB', borderBottom: '1px solid #E5E7EB' }}>
            Completed ({done.length})
          </div>
          {done.map(t => (
            <div key={t.id} style={{ padding: '10px 16px', borderBottom: '1px solid #F3F4F6', display: 'flex', alignItems: 'center', gap: 10, opacity: 0.6 }}>
              <CheckSquare size={15} color="#4FA038" style={{ flexShrink: 0 }} />
              <div>
                <div style={{ fontSize: 12, color: '#6C757D', textDecoration: 'line-through' }}>{t.title}</div>
                {t.completed_at && <div style={{ fontSize: 10, color: '#ADB5BD' }}>Completed {new Date(t.completed_at).toLocaleDateString()}</div>}
              </div>
            </div>
          ))}
        </div>
      )}
      {tasks.length === 0 && <div style={{ ...emptyMsg, padding: 30 }}>No tasks yet.</div>}
    </div>
  );
}

// ─── 7. Notes Pane ───────────────────────────────────────────────────────────

// ⚠ `canUpdate` was ABSENT from this signature entirely, while its sibling
// TasksPane one line above the call site received it. So the Add-Note box
// rendered for every viewer, including roles without `crm:update` — and
// `crm:contacts:add-note` is gated on exactly that permission, so the button
// existed only to return ACCESS_DENIED. Defaults to FALSE, deliberately: a
// permissive default is what produced the bug in ContactDrawer.
function NotesPane({ contactId, notes, onReload, canUpdate = false, onDirtyChange }) {
  const [newNote, setNewNote] = useState('');
  /*
   * Report "there is unsaved text" upward as a BOOLEAN — never the text.
   *
   * ⚠ Reported on CHANGE, and cleared on unmount. Without the cleanup, leaving
   * the Notes tab with a draft would leave the parent permanently dirty and
   * every later reload would prompt for a note that no longer exists — a false
   * alarm that trains people to click through the real one.
   */
  useEffect(() => {
    if (typeof onDirtyChange === 'function') onDirtyChange(newNote.trim().length > 0);
    return () => { if (typeof onDirtyChange === 'function') onDirtyChange(false); };
  }, [newNote, onDirtyChange]);
  const [saving, setSaving] = useState(false);

  const addNote = async () => {
    if (!newNote.trim()) return;
    setSaving(true);
    try {
      await window.zignagenixAPI.addCRMContactNote(contactId, newNote.trim());
      setNewNote('');
      onReload();
    } catch (e) { alert('Error: ' + e.message); }
    finally { setSaving(false); }
  };

  return (
    <div style={{ padding: 16 }}>
      {/* Add Note — only for viewers who can actually write one. */}
      {canUpdate && (
        <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
          <textarea value={newNote} onChange={e => setNewNote(e.target.value)}
            placeholder="Add an internal note... (not visible to the contact)"
            style={{ ...inp, minHeight: 60, resize: 'vertical', flex: 1 }} />
          <button onClick={addNote} disabled={saving || !newNote.trim()} style={{ ...btnPrimary, alignSelf: 'flex-end' }}>
            <Send size={12} /> {saving ? '...' : 'Save'}
          </button>
        </div>
      )}

      {/* Existing Notes */}
      {notes ? (
        <div style={{ whiteSpace: 'pre-wrap', fontSize: 12, color: '#374151', lineHeight: 1.8 }}>
          {notes.split('\n\n').map((block, i) => {
            const match = block.match(/^\[(.+?)\]\s*(.*)/s);
            return (
              <div key={i} style={{ padding: '10px 14px', background: i % 2 === 0 ? '#F9FAFB' : '#fff', borderRadius: 8, marginBottom: 6, border: '1px solid #F3F4F6' }}>
                {match ? (
                  <>
                    <div style={{ fontSize: 10, color: '#ADB5BD', fontWeight: 600, marginBottom: 4 }}>{match[1]}</div>
                    <div>{match[2]}</div>
                  </>
                ) : <div>{block}</div>}
              </div>
            );
          })}
        </div>
      ) : (
        // "Add one above" is only true when the box is rendered.
        <div style={{ color: '#ADB5BD', fontSize: 12 }}>
          {canUpdate ? 'No notes yet. Add one above.' : 'No notes yet.'}
        </div>
      )}
    </div>
  );
}

// ─── 8. Workflows Pane ───────────────────────────────────────────────────────

function WorkflowsPane({ workflowRuns }) {
  if (workflowRuns.length === 0) return <div style={{ ...emptyMsg, padding: 30 }}>Not enrolled in any workflows.</div>;

  const statusColors = { running: '#2E8B98', completed: '#4FA038', failed: '#DC3545', stopped: '#6C757D' };

  return (
    <div style={{ maxHeight: 500, overflowY: 'auto' }}>
      {workflowRuns.map((wr, i) => (
        <div key={wr.id || i} style={{ padding: '12px 16px', borderBottom: '1px solid #F3F4F6', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <div style={{ fontSize: 13, fontWeight: 600, color: '#212529' }}>{wr.workflow_name || `Workflow #${wr.workflow_id}`}</div>
            <div style={{ display: 'flex', gap: 6, marginTop: 4 }}>
              <Badge text={wr.status || 'running'} color={statusColors[wr.status] || '#6C757D'} />
              <span style={{ fontSize: 10, color: '#ADB5BD' }}>
                Started {wr.started_at ? new Date(wr.started_at).toLocaleDateString() : '—'}
              </span>
            </div>
          </div>
          <Zap size={14} color={statusColors[wr.status] || '#6C757D'} />
        </div>
      ))}
    </div>
  );
}

// ─── 10. Payments Pane (placeholder) ─────────────────────────────────────────

function PaymentsPane() {
  return (
    <div style={{ ...emptyMsg, padding: 30 }}>
      <CreditCard size={28} color="#ADB5BD" style={{ marginBottom: 8 }} />
      <div>Payment history will appear here once Stripe integration is connected.</div>
    </div>
  );
}
